import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MoreIcon } from "@/shared/assets/icons";
import { Button, Dropdown, DropdownItem } from "@/shared/ui";
import { useAuth } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts";
import { getAvailableActions, type HiringAction } from "../../../utils";
import { type GetApplicationsResponse } from "../../../types";

interface Props {
  application: GetApplicationsResponse;
  isLoading: boolean;
  onAction: (action: HiringAction, applicationId: number, onClose?: () => void) => void;
  onViewDetails?: (applicationId: number) => void;
  onDownloadProfile?: (id: number) => void;
  isDownloading?: boolean;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  direction?: "top" | "bottom";
}

export default function ApplicationActionsDropdown({
  direction = "bottom",
  application,
  isLoading,
  onAction,
  onViewDetails,
  isOpen: controlledOpen,
  onToggle,
}: Props) {
  const { t } = useTranslation("HiringPage");
  const { hasPermission } = useAuth();
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setIsOpen = onToggle || setInternalOpen;

  const { actions: allActions } = getAvailableActions(application, isLoading, t);
  
  const actions = allActions.filter((action) => {
    if (action.id === "approve" || action.id === "reject" || action.id === "request_revision") {
      return hasPermission(PERMISSIONS.HR.CREATE_EMPLOYEE);
    }
    
    if (action.id === "create_contract") {
      return hasPermission(PERMISSIONS.HR.CREATE_CONTRACT);
    }

    return true;
  });

  if (actions.length === 0) return null;

  function handleActionClick(action: HiringAction) {
    if (action === "view_details" && onViewDetails) {
      onViewDetails(application.id);
    } else {
      onAction(action, application.id);
    }
    setIsOpen(false);
  }

  return (
    <Dropdown open={isOpen && !isLoading} onClose={() => setIsOpen(false)} direction={direction}>
      <Button
        variant="secondary"
        isIconButton
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        disabled={isLoading}
        className="w-8! radius-xs! p-0!">
        <MoreIcon />
      </Button>

      {actions.map((action) => (
        <DropdownItem key={action.id} onClick={() => handleActionClick(action.id)}>
          {action.label}
        </DropdownItem>
      ))}
    </Dropdown>
  );
}
